// Pine-faithful precedence (entry-level sizing > strategy_config > fallback)
// means a Properties → Default order size change written ONLY to
// strategy_config would be silently ignored whenever the entry blocks
// declare their own sizing. Two unwanted choices fall out: either flip the
// precedence (loses Pine parity + breaks the regression we locked) or leave
// the Properties dropdown silently inert.
//
// This module is the third path — AUTO-TARGET WRITE: when Properties edits
// `default_qty_type` / `default_qty_value`, mirror the change onto every
// entry block that declares its own sizing (the effective source) AND keep
// strategy_config in sync. Precedence is unchanged; Properties just stops
// being a no-op.
//
// Pure functions, no React, no IO — kept here for test-conversion.mjs to
// exercise them straight from node alongside the engine/converter parity
// cases.

import type { Block, BlockData } from '../../types';
import { BlockType } from '../../types';

// Fields whose presence on an ENTRY block tells the engine "this entry has
// its own sizing — don't fall back to strategy_config." Same set the engine
// (sizing.ts), converter (graphCompiler.declaresSizing), and Run Parameters
// already use.
const SIZING_DECLARING_FIELDS = [
  'position_sizing_method', 'quantity_type', 'quantity_value',
  'equity_percent', 'risk_per_trade', 'fixed_risk_usd', 'atr_size_multiplier',
  // TSL v1.2-pre §X — an entry that references a qty_calculator block
  // also "declares its own sizing" from the engine's point of view.
  'qtyCalculatorBlockId',
] as const;

export function entryDeclaresSizing(d: Record<string, unknown>): boolean {
  return SIZING_DECLARING_FIELDS.some(k => d[k] !== undefined);
}

/** True iff any entry references a qty_calculator block — used by UI to
 *  distinguish "Properties dropdown is mirrored onto entries" (Pine-style
 *  method) from "Properties dropdown is informational only because a qty
 *  calculator is wired and wins outright". */
export function anyEntryReferencesQtyCalculator(blocks: Block[]): boolean {
  return blocks.some(b =>
    b.type === BlockType.ENTRY
    && ((b.data ?? {}) as Record<string, unknown>).qtyCalculatorBlockId !== undefined,
  );
}

export type EffectiveSizingSource = 'qty_calculator' | 'entry' | 'config' | 'none';

/** Which TSL layer drives sizing today, given the strategy's current blocks.
 *  Order mirrors the engine's runtime precedence: qty_calculator > entry
 *  block > strategy_config > engine fallback. */
export function effectiveSizingSource(blocks: Block[]): EffectiveSizingSource {
  if (anyEntryReferencesQtyCalculator(blocks)) return 'qty_calculator';
  const entries = blocks.filter(b => b.type === BlockType.ENTRY);
  const anyEntrySized = entries.some(b =>
    entryDeclaresSizing((b.data ?? {}) as Record<string, unknown>),
  );
  if (anyEntrySized) return 'entry';
  if (blocks.some(b => b.type === BlockType.STRATEGY_CONFIG)) return 'config';
  return 'none';
}

// Pine-compatible per-method routing: the `default_qty_value` magnitude
// belongs to a different field on the entry block depending on which sizing
// method is active. Matches the engine's `pickNum(eb.<method-specific>, …, dqv)`
// fallback ordering in src/engine/sizing.ts.
function entryValueFieldForMethod(method: unknown): string {
  const m = String(method ?? '');
  if (m === 'percent_equity') return 'equity_percent';
  if (m === 'risk_based' || m === 'atr_risk' || m === 'pine_futures') return 'risk_per_trade';
  return 'quantity_value';
}

/**
 * AUTO-TARGET WRITE for Properties → Default order size.
 *
 * Always updates `strategy_config` so the persisted "default" stays in sync
 * with what the user picked. When ANY entry block declares its own sizing,
 * also propagates the change to EVERY such entry block — that's the layer
 * the engine actually reads, so without this propagation the Properties
 * dropdown silently does nothing while entries override config.
 *
 * Per-method value routing on entries:
 *   percent_equity → equity_percent
 *   risk_based / atr_risk / pine_futures → risk_per_trade
 *   everything else (fixed, contracts, currency, fixed_risk) → quantity_value
 * `quantity_value` is also written as the canonical synonym so any downstream
 * code path that reads it sees the same number.
 *
 * On a `default_qty_type` change (method switch), the strategy_config's
 * current `default_qty_value` is propagated into the NEW method's field on
 * each entry block — so a value set under one method carries over to the
 * next instead of resurrecting a stale method-specific number.
 *
 * Pure: returns a NEW blocks array. Pine precedence (entry > config) is
 * unchanged because we still write to entries; we just also keep them
 * aligned with the Properties choice.
 */
export function applyDefaultOrderSizePatch(
  blocks: Block[],
  key: 'default_qty_type' | 'default_qty_value',
  value: unknown,
): Block[] {
  const cfg = blocks.find(b => b.type === BlockType.STRATEGY_CONFIG);
  if (!cfg) return blocks;
  const cfgData = (cfg.data ?? {}) as Record<string, unknown>;

  // Detect whether ANY entry overrides — drives whether we propagate to
  // entries at all. EXCEPTION: when an entry references a qty_calculator
  // block, that calculator wins outright regardless of entry-level method
  // fields, so writing method/value onto the entries would be silently
  // ineffective. In that case fall back to writing config only and let the
  // UI surface "Effective sizing source: Qty calculator block" so the user
  // sees that the Properties dropdown is informational until they unwire
  // the calculator.
  const qtyCalcWired = anyEntryReferencesQtyCalculator(blocks);
  const targetEntries = !qtyCalcWired && blocks.some(
    b => b.type === BlockType.ENTRY && entryDeclaresSizing((b.data ?? {}) as Record<string, unknown>),
  );

  // What method is "current" AFTER this patch lands? Used to pick the right
  // value field on entries.
  const methodAfter = key === 'default_qty_type' ? value : cfgData.default_qty_type;

  // What value is "current" AFTER this patch lands? Used when propagating a
  // method change to also seed the new method's value field on entries.
  const valueAfter = key === 'default_qty_value' ? value : cfgData.default_qty_value;

  return blocks.map(b => {
    // (a) strategy_config — always update the chosen field.
    if (b.id === cfg.id) {
      return { ...b, data: { ...(b.data ?? {}), [key]: value } as BlockData };
    }
    // (b) entry blocks that own their sizing — propagate Properties choice.
    if (
      targetEntries
      && b.type === BlockType.ENTRY
      && entryDeclaresSizing((b.data ?? {}) as Record<string, unknown>)
    ) {
      const nd: Record<string, unknown> = { ...(b.data ?? {}) };
      if (key === 'default_qty_type') {
        nd.position_sizing_method = value;
        // Carry the current default_qty_value into the NEW method's slot, so
        // changing the method doesn't leave the entry sized off a stale
        // method-specific field. Skip if there's no value to propagate.
        if (valueAfter !== undefined && valueAfter !== null) {
          const newFld = entryValueFieldForMethod(value);
          nd[newFld] = valueAfter;
          if (newFld !== 'quantity_value') nd.quantity_value = valueAfter;
        }
      } else {
        // key === 'default_qty_value' — route to the active method's field.
        const fld = entryValueFieldForMethod(methodAfter);
        nd[fld] = value;
        if (fld !== 'quantity_value') nd.quantity_value = value;
      }
      return { ...b, data: nd as BlockData };
    }
    return b;
  });
}
